import React, { useEffect, useState } from 'react';
import { updateCustomerInfo } from '../../../services/customer.service';
import { getPharmacyList } from '../../../services/user.service';
import Dialog from '../../ui/Dialog';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay.component';
import {
    Button,
    makeStyles,
    TextField,
    Typography,
    Grid,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    CircularProgress
} from '@material-ui/core';

interface FormErrors {
    patient_name?: string;
    mobile_number?: string;
    chosen_language?: string;
    retail_pharmacy_id?: string;
}

interface PatientDetails {
    patient_name: string;
    email_address: string;
    mobile_number: string | null;
    chosen_language: string;
    retail_pharmacy_chain_subtext: string;
    retail_pharmacy_id: string;
}

interface ApiError {
    inputs?: Record<string, string>;
    message?: string;
}

interface PharmacyOption {
    id: string;
    name: string;
    chain: string;
}

const UpdateCustomerDetails = ({ data }) => {
    const [openModal, setOpenModal] = useState(false);
    const [openConfirmation, setConfirmation] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [errorInputMessage, setErrorInputMessage] = useState<FormErrors>({});
    const [isSaving, setIsSaving] = useState(false);

    const useStyles = makeStyles(() => ({
        field: {
            marginBottom: 10,
            width: '100%'
        },
        requiredLabel: {
            '& .MuiInputLabel-asterisk': {
                color: 'red'
            }
        }
    }));

    const classes = useStyles();

    const {
        patient_name,
        mobile_number,
        email,
        patient_id,
        program_id,
        metadata = {},
        chosen_language: root_chosen_language,
        retail_pharmacy_id: root_retail_pharmacy_id
    } = data;

    const {
        chosen_language,
        preferred_language,
        email_address,
        retail_pharmacy_chain_subtext,
        retail_pharmacy_id
    } = metadata || {};

    const [pharmacyOptions, setPharmacyOptions] = useState<PharmacyOption[]>([]);

    const programId = program_id || window.programConfig?.program_id;

    useEffect(() => {
        let isMounted = true;

        const loadPharmacies = async () => {
            if (!programId?.includes('sybrava')) return;

            try {
                const response = await getPharmacyList({ program_id: programId });
                const options = (response?.result || [])
                    .map((hcp) => {
                        const name = hcp.full_name?.trim() || '';
                        return {
                            id: hcp.id,
                            name,
                            chain: name,
                        };
                    })
                    .filter((option) => option.id && option.name);

                if (isMounted) {
                    setPharmacyOptions(options);
                }
            } catch (error) {
                console.log(error);
            }
        };

        loadPharmacies();

        return () => {
            isMounted = false;
        };
    }, [programId]);

    const getPharmacyChain = (id: string, text: string) => {
        if (text) return text;
        const match = pharmacyOptions.find(p => p.id === id);
        return match ? match.chain : '';
    };

    const finalPharmacyId = retail_pharmacy_id || root_retail_pharmacy_id || '';
    const finalChosenLanguage = chosen_language || root_chosen_language || preferred_language || '';

    const [patientDetails, setPatientDetails] = useState<PatientDetails>({
        patient_name: patient_name || '',
        email_address: email_address || email || '',
        mobile_number: mobile_number === 'null' ? null : mobile_number,
        chosen_language: finalChosenLanguage,
        retail_pharmacy_chain_subtext: getPharmacyChain(finalPharmacyId, retail_pharmacy_chain_subtext),
        retail_pharmacy_id: finalPharmacyId,
    });

    const onSubmit = async () => {
        const errors: FormErrors = {};
        if (!patientDetails.patient_name) errors.patient_name = 'Full Name is required';
        if (!patientDetails.mobile_number) errors.mobile_number = 'Mobile Number is required';
        if (!patientDetails.chosen_language) errors.chosen_language = 'Language is required';
        if (!patientDetails.retail_pharmacy_id) errors.retail_pharmacy_id = 'Pharmacy is required';

        if (Object.keys(errors).length > 0) {
            setErrorInputMessage(errors);
            return;
        }

        try {
            setLoading(true);
            setIsSaving(true);

            const res = await updateCustomerInfo({
                program_id: programId,
                customer_id: patient_id,
                payload: {
                    mobile_number: patientDetails.mobile_number || undefined,
                    chosen_language: patientDetails.chosen_language,
                    metadata: {
                        patient_name: patientDetails.patient_name,
                        email_address: patientDetails.email_address,
                        retail_pharmacy_id: patientDetails.retail_pharmacy_id
                    }
                }
            });

            if (res) {
                setOpenModal(false);
                setConfirmation(res.message);
            }
        } catch (error) {
            const err = error as ApiError;
            if (err?.inputs && err.message === 'ValidationError') {
                setErrorInputMessage(err.inputs);
            } else if (err?.message) {
                alert(err.message);
            }
        } finally {
            setLoading(false);
            setIsSaving(false);
        }
    };

    const onClick = () => {
        setPatientDetails({
            patient_name: patient_name || '',
            email_address: email_address || email || '',
            mobile_number: mobile_number === 'null' ? null : mobile_number,
            chosen_language: finalChosenLanguage,
            retail_pharmacy_chain_subtext: getPharmacyChain(finalPharmacyId, retail_pharmacy_chain_subtext),
            retail_pharmacy_id: finalPharmacyId,
        });
        setErrorInputMessage({});
        setOpenModal(true);
    };

    const onFieldInput = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const name = e.target.name as keyof PatientDetails;
        const value = e.target.value as string;

        if (!name) return;

        if (errorInputMessage[name]) {
            setErrorInputMessage(prev => ({ ...prev, [name]: undefined }));
        }

        if (name === 'retail_pharmacy_id') {
            const selectedPharmacy = pharmacyOptions.find(p => p.id === value);
            setPatientDetails(prev => ({
                ...prev,
                [name]: value,
                retail_pharmacy_chain_subtext: selectedPharmacy
                    ? selectedPharmacy.chain
                    : prev.retail_pharmacy_chain_subtext
            }));
        } else {
            setPatientDetails(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleClose = (reload: boolean) => {
        setOpenModal(false);
        setConfirmation(false);
        if (reload) window.location.reload();
    };

    const formContent = (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    required
                    label="Full Name"
                    name="patient_name"
                    value={patientDetails.patient_name}
                    variant="outlined"
                    className={classes.field}
                    onChange={onFieldInput}
                    error={!!errorInputMessage.patient_name}
                    InputLabelProps={{ className: classes.requiredLabel }}
                />
                <Typography variant="caption" display="block" gutterBottom color="error">
                    {errorInputMessage?.patient_name || ''}
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    label="Mobile Number"
                    required
                    name="mobile_number"
                    value={patientDetails.mobile_number || ''}
                    variant="outlined"
                    className={classes.field}
                    onChange={onFieldInput}
                    placeholder="60XXXXXXXXX"
                    error={!!errorInputMessage.mobile_number}
                    InputLabelProps={{ className: classes.requiredLabel }}
                />
                <Typography variant="caption" display="block" gutterBottom color="error">
                    {errorInputMessage?.mobile_number || ''}
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    disabled
                    label="Email Address"
                    name="email_address"
                    value={patientDetails.email_address}
                    variant="outlined"
                    className={classes.field}
                    onChange={onFieldInput}
                />
            </Grid>

            <Grid item xs={12}>
                <FormControl variant="outlined" className={classes.field} fullWidth>
                    <InputLabel className={classes.requiredLabel}>
                        Choose Your Preferred Language
                    </InputLabel>
                    <Select
                        name="chosen_language"
                        value={patientDetails.chosen_language}
                        onChange={onFieldInput}
                        error={!!errorInputMessage.chosen_language}
                        label="Choose Your Preferred Language"
                    >
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="my">Malay</MenuItem>
                        <MenuItem value="cn">Chinese</MenuItem>
                    </Select>
                </FormControl>
                <Typography variant="caption" display="block" gutterBottom color="error">
                    {errorInputMessage?.chosen_language || ''}
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <FormControl variant="outlined" className={classes.field} fullWidth>
                    <InputLabel className={classes.requiredLabel}>
                        Preferred Retail Pharmacy Chain
                    </InputLabel>
                    <Select
                        name="retail_pharmacy_id"
                        value={patientDetails.retail_pharmacy_id}
                        onChange={onFieldInput}
                        label="Preferred Retail Pharmacy Chain"
                    >
                        {pharmacyOptions.map(option => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="caption" display="block" gutterBottom color="error">
                    {errorInputMessage?.retail_pharmacy_id || ''}
                </Typography>
            </Grid>
        </Grid>
    );

    const formButtons = (
        <>
            <Button onClick={() => handleClose(false)} variant="contained">
                Close
            </Button>
            <Button
                startIcon={isSaving ? <CircularProgress size={20} /> : null}
                disabled={isSaving}
                onClick={onSubmit}
                variant="contained"
                color="secondary"
            >
                Submit
            </Button>
        </>
    );

    return (
        <>
            <Dialog
                open={openModal}
                title="Update Patient"
                content={formContent}
                children={formButtons}
                onSubmit={onSubmit}
                onClose={() => handleClose(false)}
            />

            <Dialog
                open={openConfirmation}
                title="Confirmation"
                content="Customer details successfully updated!"
                children={
                    <Button onClick={() => handleClose(true)} variant="contained">
                        Close
                    </Button>
                }
                onSubmit={onSubmit}
                onClose={() => handleClose(true)}
            />

            <Button variant="contained" onClick={onClick} style={{ marginRight: 8 }}>
                Update Details
            </Button>

            {isLoading && <LoadingOverlay />}
        </>
    );
};

export default UpdateCustomerDetails;
